// Shared invitation wire shapes (ADR-0116).
//
// Two core-api routes stamp a per-store role attribute before fanning an
// invitation out to auth-svc's POST /auth/invitations:
//   - POST /admin/orgs/:id/invitations   (admin.ts, platform-admin only)
//   - POST /organizations/me/invitations (organizations.ts, self-service)
//
// Both report the exact same tri-state per-asset outcome and both accept
// (or, for the self-service route, sometimes compute) the same assetIds
// shape. Extracted here so the two routes' response contracts can't drift
// into two enums that mean the same thing but don't type-check as the
// same value — see apps/core-api/src/lib/invitation-asset-stamp.ts for
// the shared stamping logic that produces this shape.
import { z } from "zod";
import { emailSchema, invitationAffiliationSchema, isEmailRequiredInviteRole, roleSchema, } from "./auth.js";
// Per-asset outcome of an invite-time role-attribute stamp. `stamped` =
// the role-appropriate attribute (jefe_email or supervisor_email) written
// and will bind on acceptance; `already_assigned` = the store already has
// a bound holder for that same role (skipped, never overwritten);
// `not_found` = the id is not an asset of the caller's org (e.g. a stale
// or cross-org id). Surfacing this prevents the silent partial-success the
// IS NULL stamp guard would otherwise produce.
export const invitationAssetResultSchema = z.object({
    assetId: z.string().uuid(),
    status: z.enum(["stamped", "already_assigned", "not_found"]),
});
// POST /organizations/me/invitations — self-service invite into the
// caller's own org (ADR-0116). Companion to the platform-admin
// POST /admin/orgs/:id/invitations (adminOrgInvitationCreateRequestSchema,
// admin.ts), which takes an explicit org id and is gated on
// req.requirePlatformAdmin(); this one is keyed on the caller's own
// auth.organizationId and gated on req.requireAuth() + the recipe /
// cardinality / containment checks the route itself applies.
//
// `email` is optional — auth-svc's underlying POST /auth/invitations
// already supports token-only, share-by-link invitations
// (createInvitationRequestSchema, body.email omitted). The refinement
// below narrows that back to required for the two asset-scoped roles
// (store_manager, facility_supervisor): the per-store stamp writes the
// invite's email into assets.attributes.{jefe_email,supervisor_email}
// so ADR-0036's post-acceptance backfill can match the accepting user by
// email — an asset-scoped invite with no email would stamp an empty
// string nothing can ever match, silently orphaning the store assignment.
export const orgSelfServiceInvitationCreateRequestSchema = z
    .object({
    email: z.string().trim().toLowerCase().email().optional(),
    role: roleSchema,
    displayName: z.string().trim().min(1).max(120).optional(),
    // The stores to assign a store_manager or facility_supervisor to.
    // Optional for facility_supervisor only — the route computes a
    // default (every scoped, currently-unassigned store) when omitted.
    // Required (and capped to exactly one by the route) for
    // store_manager. Rejected outright for every other role.
    assetIds: z.array(z.string().uuid()).min(1).max(500).optional(),
    // ADR-0164: the brand a brand-scoped role (brand_manager) is invited
    // to. Required for a brand-scoped role, rejected for any other —
    // auth-svc enforces it (invitationBrandScopeDenial in ./auth.js).
    scopeBrandId: z.string().uuid().optional(),
    // ADR-0165: the brand a supervisor belongs to (identity, not scope).
    // Optional: when absent, core-api derives it from the stores (or the
    // org's only brand). Rejected for a role without a brand of
    // affiliation. Stays in core-api — never sent to auth-svc.
    affiliationBrandId: invitationAffiliationSchema.shape.id.optional(),
})
    .strict()
    // A brand manager is always invited to a specific person (founder
    // 2026-10-19), so brand-scoped roles join the asset-scoped ones here.
    .refine((d) => !isEmailRequiredInviteRole(d.role) || d.email !== undefined, {
    message: "email is required for asset-scoped and brand-scoped roles",
    path: ["email"],
});
export const orgSelfServiceInvitationCreateResponseSchema = z.object({
    acceptUrl: z.string(),
    // Present only for store_manager / facility_supervisor invitations —
    // omitted for admin / company_manager, exactly like
    // adminOrgInvitationCreateResponseSchema.
    assetResults: z.array(invitationAssetResultSchema).optional(),
    // Sin flags de cuenta (p2): ver createInvitationResponseSchema en
    // auth.ts — revelaban si el correo tenía cuenta en OTRA organización.
});
// Teléfono de contacto del propietario (ADR-0130). Una sola regla para
// los tres lados que lo tocan: el formulario de activación del dashboard
// (validación inline), el body de `POST /assets/:id/owner-invitation`
// (400 si no cumple) y la proyección del portal de contratistas
// (apps/core-api/src/lib/owner-contact.ts), que pinta «Llamar» sólo si el
// valor guardado tiene al menos estos dígitos. Aceptar acá un valor que
// el portal después descarta sería guardar un teléfono que nadie ve.
//
// Un celular chileno son 9 dígitos (11 con +56); ocho es el piso que
// rechaza un "s/i" o un "-" sin adivinar formato. El valor se guarda
// recortado pero tal cual se escribió (espacios, guiones, +56): la misma
// forma en que la planilla de unidades escribe `attributes.owner_phone`.
export const OWNER_PHONE_MIN_DIGITS = 8;
const OWNER_PHONE_CHARS = /^\+?[\d\s().-]+$/;
export function isValidOwnerPhone(raw) {
    const value = raw.trim();
    return (value.length <= 40 &&
        OWNER_PHONE_CHARS.test(value) &&
        value.replace(/\D/g, "").length >= OWNER_PHONE_MIN_DIGITS);
}
export const ownerPhoneSchema = z
    .string()
    .trim()
    .refine(isValidOwnerPhone, {
    message: `phone must contain at least ${OWNER_PHONE_MIN_DIGITS} digits and only +, digits, spaces, dashes, dots or parentheses`,
});
// POST /assets/:id/owner-invitation — activar una unidad (ADR-0124).
//
// El body NO lleva `role` ni `assetIds`: los dos salen del servidor. El
// rol se resuelve del primitivo de receta
// (`resolveAssetPartyMemberRole`) y la unidad es el `:id` de la URL.
// Ése es el contrato entero de la decisión: "la activación ES la
// invitación", así que no hay nada que elegir al invitar más allá de a
// QUIÉN. Un body que aceptara `role` reabriría, por la puerta del
// cliente, la ambigüedad que el primitivo cierra.
//
// `.strict()` por lo mismo — un `role` o un `assetIds` de más no se
// ignora en silencio, se rechaza.
export const assetOwnerInvitationCreateRequestSchema = z
    .object({
    // Lo asevera la inmobiliaria, no el titular. auth-svc lo toma como
    // `displayName` autoritativo de la cuenta; el propietario lo
    // rectifica después por PATCH /auth/me.
    ownerName: z.string().trim().min(1).max(120),
    // `emailSchema`, no un `z.string().email()` propio. La diferencia no
    // es cosmética: emailSchema normaliza a minúsculas, y el backfill
    // post-aceptación (ADR-0036, internal-auth-backfill.ts) compara el
    // email del usuario que aceptó contra `attributes.propietario_email`
    // ya normalizado. Un email estampado con mayúsculas no matchea
    // nunca: la unidad queda con el email escrito y `owner_user_id`
    // permanentemente NULL, y el guard `IS NULL` del propio backfill
    // hace que el reintento tampoco lo arregle.
    ownerEmail: emailSchema,
    // Opcional (ADR-0130): el portal de contratistas muestra «Llamar»
    // sólo si la unidad tiene `attributes.owner_phone`. Ausente = no
    // tocar el teléfono que ya tenga la unidad (p. ej. el que trajo la
    // planilla); no hay forma de borrarlo por esta ruta, a propósito.
    ownerPhone: ownerPhoneSchema.optional(),
})
    .strict();
// Alias deliberado, no una copia: la respuesta de activar una unidad ES
// la respuesta de una invitación self-service (mismo `acceptUrl`, mismo
// `assetResults` tri-estado), porque detrás es literalmente el mismo
// camino de ADR-0116 con el rol y la unidad resueltos por el servidor.
// Dos objetos Zod estructuralmente iguales derivarían en dos tipos que
// no unifican — y el handler de la ruta nueva compone exactamente la
// misma función que el de `/organizations/me/invitations`.
export const assetOwnerInvitationCreateResponseSchema = orgSelfServiceInvitationCreateResponseSchema;
// --- Brand of affiliation of existing members (ADR-0165 S5) -------------
//
// GET /organizations/me/brand-affiliations — every linked supervisor's
// brand, for the Equipo roster. Only org-scoped roles (and platform
// admins) read it: a brand manager must not see another brand's people
// (#0777). PUT /organizations/me/members/:userId/brand-affiliation sets or
// replaces one member's brand; same callers.
export const memberBrandAffiliationSchema = z.object({
    userId: z.string().uuid(),
    brandId: invitationAffiliationSchema.shape.id,
});
export const memberBrandAffiliationsResponseSchema = z.object({
    affiliations: z.array(memberBrandAffiliationSchema),
});
export const memberBrandAffiliationUpdateBodySchema = z
    .object({ brandId: invitationAffiliationSchema.shape.id })
    .strict();
//# sourceMappingURL=invitations.js.map